import { getElectionToggles, TheNightlySection } from '@news-mono/common'
import {
    getCoralStoryId,
    getInlineEventAds,
    inlineArticleAds,
    sectionThemeOverrider,
    theNightlySmallSectionHeaderOverride,
    metrics,
    InlineContentTypes,
    InlineTNRegisterAccount,
    IconChevronUpTN,
    teadsAdSlotID,
    calcRem,
    InlineTNNewsletterBanner,
} from '@news-mono/component-library'
import {
    AdTargeting,
    BaseClientConfig,
    ElectionDefinition,
    getPaginatedCanonicalUrl,
    GetRouteAdTargeting,
    isElectionFeatureEnabled,
    PageType,
    parsePageParam,
    Product,
    publicationAdKind,
    publicationAdPageType,
    RenderTarget,
    TogglesReduxState,
} from '@news-mono/web-common'
import queryString from 'query-string'
import {
    ArticleLikeV4DTO,
    PageMetaDTO,
    PageMetaLinkDTO,
} from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { getNoIndexMeta } from './allowList'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import H from 'history'
import { getLatestEditionSegment } from '../../home/get-editions-segment'
import { sectionNavItems, subTopicNavItems } from '../../../navigation'
import { cardList } from '../../home/get-cardList-segment'
import { CheckedComponentInformation } from '@west-australian-newspapers/json-react-layouts'
import React from 'react'
import { getUnloadedElectionDefinition } from '../../elections/routes'

const MAX_LIVE_BLOG_PAGES = 100
const LIVE_BLOG_PAGE_SIZE = 10

// Used for Quicklinks
export const publicationPageType = 'publication'

export const createArticleRoute = ({
    article,
    config,
    section,
    location,
    getAdTargeting,
    pageMeta,
    renderTarget,
    toggles,
}: {
    article: ArticleLikeV4DTO
    config: BaseClientConfig
    section: TheNightlySection
    location: H.Location<any>
    getAdTargeting: GetRouteAdTargeting
    renderTarget: RenderTarget
    pageMeta?: PageMetaDTO
    toggles?: TogglesReduxState
}): PageType<TheNightlySection> => {
    const featureState = toggles && toFeatureState(toggles)

    const inApp = renderTarget === 'app'

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        publicationAdKind[article.kind],
        section,
        article.topics.primary,
        ...article.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const isFeature = (feature: string | undefined) =>
        (featureState &&
            feature !== undefined &&
            isFeatureEnabled(featureState, feature, false)) ??
        false

    const standardInlineArticleAdsEnabled = isFeature('tn-article-body-ads')
    const enableExtraInlineArticleAds = standardInlineArticleAdsEnabled

    const enableTeadsAds = isFeature('teads-article-ads')
    const enableTheGameDriver =
        article.topics.primary.id.includes('sport') &&
        article?.classification?.kind !== 'sales' &&
        isFeature('the-game-article-driver')

    const enableTravelNewsletterInlineCta =
        article.topics.primary.id.includes('travel') &&
        isFeature('the-nightly-travel-newsletter-inline-cta')

    const enablePollyRaterBanner = isFeature('pollie-rater-drivers')

    // Election Content
    const getFeatureValue = (feature: string | undefined) =>
        (featureState &&
            feature !== undefined &&
            featureState[feature]?.value.string()) ??
        undefined
    const wanElectionId =
        getFeatureValue('article-header-wan-election-id') ||
        'federal-election-2025'
    const electionDefinition = getUnloadedElectionDefinition(wanElectionId)
    const isElectionFeature = (
        feature: keyof ReturnType<typeof getElectionToggles> | undefined,
    ) =>
        (featureState &&
            feature !== undefined &&
            isElectionFeatureEnabled(
                toggles,
                electionDefinition.electionId,
                feature,
            )) ??
        false
    const enableElectionNavigation = isElectionFeature('election-navigation')
    const enableElectionSectionHeadToHead = isElectionFeature(
        'election-section-head-to-head',
    )
    const enableElectionSectionHeadToHeadCountdown = isElectionFeature(
        'election-head-to-head-countdown',
    )

    const extraInlineArticleAds = [
        inlineArticleAds({
            insertAfter: 14,
            noticePosition: 'below-center',
            hiddenUntilLoaded: false,
            mastHead: 'thenightly',
            slotID: 'article-mrec-one',
            size: enableExtraInlineArticleAds
                ? 'leaderboardAbove768MrecBelow'
                : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
            lazyLoadingDistance: 0,
        }),
        inlineArticleAds({
            insertAfter: 23,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thenightly',
            slotID: 'article-mrec-two',
            size: enableExtraInlineArticleAds
                ? 'leaderboardAbove768MrecBelow'
                : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
            lazyLoadingDistance: 0,
        }),
        inlineArticleAds({
            insertAfter: 32,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thenightly',
            slotID: 'article-mrec-three',
            size: enableExtraInlineArticleAds
                ? 'leaderboardAbove768MrecBelow'
                : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
            lazyLoadingDistance: 0,
        }),
        inlineArticleAds({
            insertAfter: 41,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thenightly',
            slotID: 'article-mrec-four',
            size: enableExtraInlineArticleAds
                ? 'leaderboardAbove768MrecBelow'
                : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
            lazyLoadingDistance: 0,
        }),
        ...(enableTeadsAds
            ? [
                  inlineArticleAds({
                      insertAfter: 5,
                      noticePosition: 'below-center',
                      hiddenUntilLoaded: false,
                      mastHead: 'thenightly',
                      slotID: teadsAdSlotID,
                      size: 'teads',
                      pageType: publicationAdPageType[article.kind],
                  }),
              ]
            : []),
    ]

    const standardInlineArticleAds = (
        desktopEnabled: boolean,
    ): InlineContentTypes[] => {
        return [
            inlineArticleAds({
                insertAfter: 3,
                noticePosition: 'below-center',
                hiddenUntilLoaded: false,
                mastHead: 'thenightly',
                slotID: 'mrec-one',
                size: desktopEnabled
                    ? 'leaderboardAbove768MrecHalfPageBelow'
                    : 'below768MrecHalfPage',
                pageType: publicationAdPageType[article.kind],
                lazyLoadingDistance: 0,
            }),
        ]
    }

    const query = queryString.parse(location.search)
    const page = parsePageParam(query.page, MAX_LIVE_BLOG_PAGES)

    const link: PageMetaLinkDTO[] = []

    // use the canonical url of the article if provided, otherwise use the current page as the canonical
    const canonicalUrl =
        page > 1 && article._self === article.canonicalUrl
            ? getPaginatedCanonicalUrl(article._self, page)
            : article.canonicalUrl

    if (canonicalUrl) {
        link.push({ rel: 'canonical', href: canonicalUrl })
    }

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
            title: article.seoHead || undefined,
            meta: getNoIndexMeta(article.source, featureState),
            link: link,
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: publicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind: article.kind,
            // DPT-1375 extra Metadata for variant testing. Remove when finished
            requiredAccess: article.requiredAccess,
            layout: article.layoutView,
        },
        section,
        compositions: [
            layout.composition({
                type: 'scheme-overrider',
                props: {
                    override: sectionThemeOverrider(
                        'thenightly',
                        article.topics.primary.id as any as TheNightlySection,
                    ),
                },
                contentAreas: {
                    children: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                flex: {
                                    default: {
                                        flexDirection: 'column',
                                        flexGrow: 0.5,
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    ...getElectionHeader(
                                        article,
                                        enableElectionNavigation,
                                        enableElectionSectionHeadToHead,
                                        enableElectionSectionHeadToHeadCountdown,
                                        electionDefinition,
                                    ),
                                    layout.nestedComposition({
                                        type: 'luna',
                                        props: {
                                            hasAfterAside: false,
                                            asideSide: 'right',
                                        },
                                        contentAreas: {
                                            beforeAside: [
                                                ...(section !== 'default'
                                                    ? [
                                                          layout.nestedComposition(
                                                              {
                                                                  type: 'box',
                                                                  props: {
                                                                      horizontalSpacingBreakpoint:
                                                                          'sm',
                                                                      horizontalSpacing:
                                                                          'md',
                                                                  },
                                                                  contentAreas:
                                                                      {
                                                                          main: [
                                                                              layout.component(
                                                                                  {
                                                                                      type: 'thenightly-breadcrumb',
                                                                                      props: {
                                                                                          topics: article.topics,
                                                                                          nightlySectionNavItems:
                                                                                              sectionNavItems,
                                                                                          subTopicNavItems:
                                                                                              subTopicNavItems,
                                                                                      },
                                                                                  },
                                                                              ),
                                                                          ],
                                                                      },
                                                              },
                                                          ),
                                                      ]
                                                    : []),

                                                layout.component({
                                                    type: 'article',
                                                    props: {
                                                        meta: {
                                                            slug: article.slug,
                                                            kind: 'article',
                                                            section: section,
                                                            inlinePublicationContent:
                                                                [
                                                                    ...standardInlineArticleAds(
                                                                        standardInlineArticleAdsEnabled,
                                                                    ),
                                                                    ...(enableTravelNewsletterInlineCta
                                                                        ? [
                                                                              {
                                                                                  kind: 'inline-tn-newsletter-banner',
                                                                                  insertAfter: 5,
                                                                                  props: {
                                                                                      text: 'ROAM. Landing in your inbox weekly.',
                                                                                      subText:
                                                                                          'A digital-first travel magazine. Premium itineraries and adventures, practical information and exclusive offers for the discerning traveller.',
                                                                                      buttonLabel:
                                                                                          'SIGN UP NOW',
                                                                                      mode: 'inline-article-travel',
                                                                                      persistent:
                                                                                          true,
                                                                                  },
                                                                                  content:
                                                                                      [
                                                                                          {},
                                                                                      ],
                                                                              } as InlineTNNewsletterBanner,
                                                                          ]
                                                                        : [
                                                                              {
                                                                                  kind: 'inline-tn-newsletter-banner',
                                                                                  insertAfter: 3,
                                                                                  content:
                                                                                      [
                                                                                          {},
                                                                                      ],
                                                                              } as InlineTNNewsletterBanner,
                                                                          ]),
                                                                    {
                                                                        kind: 'inline-pollie-rater-banner',
                                                                        insertAfter: 8,
                                                                        toggle: enablePollyRaterBanner,
                                                                    },
                                                                    {
                                                                        kind: 'inline-the-game-banner',
                                                                        insertAfter: 10,
                                                                        props: {
                                                                            url: 'https://thegame.com.au/?utm_source=Acquisition&utm_medium=In+Article&utm_campaign=AFLW+2026&utm_term=Major+Prize&utm_content=TN',
                                                                            toggle: enableTheGameDriver,
                                                                            useImgV2:
                                                                                true,
                                                                        },
                                                                    },
                                                                    ...(inApp
                                                                        ? []
                                                                        : [
                                                                              {
                                                                                  kind: 'inline-tn-register-account',
                                                                                  insertAfter: 5,
                                                                                  content:
                                                                                      [
                                                                                          {},
                                                                                      ],
                                                                              } as InlineTNRegisterAccount,
                                                                          ]),

                                                                    ...extraInlineArticleAds,
                                                                    ...(article.kind ===
                                                                    'event'
                                                                        ? getInlineEventAds(
                                                                              Product.TheNightly,
                                                                          )
                                                                        : []),
                                                                ],
                                                        },
                                                        dataDefinitionArgs: {
                                                            publicationId:
                                                                article.id,
                                                        },
                                                        section,
                                                        isVideoStickyEnabled:
                                                            true,
                                                        liveBlogOptions: {
                                                            pageSize:
                                                                LIVE_BLOG_PAGE_SIZE,
                                                            maxPages:
                                                                MAX_LIVE_BLOG_PAGES,
                                                        },
                                                    },
                                                }),
                                                layout.nestedComposition({
                                                    type: 'box',
                                                    props: {
                                                        verticalSpacing: 'lg',
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type: 'coral',
                                                                feature:
                                                                    'comments',
                                                                props: {
                                                                    storyID:
                                                                        getCoralStoryId(
                                                                            section,
                                                                            config.publicHostname,
                                                                            article.id,
                                                                        ),
                                                                    articleCommentsEnabled:
                                                                        article.allowCommenting,
                                                                    storyURL: `${article._self}`,
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                                ...getArticleFollowers(inApp),
                                            ],
                                            afterAside: [],
                                            aside: [
                                                layout.nestedComposition({
                                                    type: 'scheme-overrider',
                                                    props: {
                                                        override:
                                                            sectionThemeOverrider(
                                                                'thenightly',
                                                                section,
                                                            ),
                                                    },
                                                    contentAreas: {
                                                        children: [
                                                            layout.nestedComposition(
                                                                {
                                                                    type: 'box',
                                                                    props: {
                                                                        hasTopSpacing:
                                                                            true,
                                                                        verticalSpacing:
                                                                            'gridGap',
                                                                        nightlyArticleSidebar:
                                                                            true,
                                                                        horizontalSpacingBreakpoint:
                                                                            'sm',
                                                                        horizontalSpacing:
                                                                            'lg',
                                                                    },
                                                                    contentAreas:
                                                                        {
                                                                            main: [
                                                                                relatedContent(
                                                                                    article.id,
                                                                                ),
                                                                                ...(enableTravelNewsletterInlineCta
                                                                                    ? []
                                                                                    : [
                                                                                          newsletterBanner(),
                                                                                      ]),
                                                                                cardList(
                                                                                    {
                                                                                        data: {
                                                                                            type: 'curation',
                                                                                            name: 'top-stories',
                                                                                            offset: 0,
                                                                                            pageSize: 5,
                                                                                        },
                                                                                        heading:
                                                                                            'The Top 5',
                                                                                        sectionHeaderAs:
                                                                                            'h4',
                                                                                        itemHeaderAs:
                                                                                            'h5',
                                                                                    },
                                                                                ),
                                                                            ],
                                                                        },
                                                                },
                                                            ),
                                                        ],
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),

                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                flex: {
                                    default: {
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.component({
                                        type: 'nightly-button',
                                        props: {
                                            variant: 'default',
                                            text: 'BACK TO TOP',
                                            color: 'primary',
                                            fill: 'text',
                                            layout: {
                                                alignSelf: 'center',
                                            },
                                            action: {
                                                type: 'button',
                                                onClick: () =>
                                                    window.scrollTo({ top: 0 }),
                                            },
                                            icon: {
                                                iconPosition: 'right',
                                                IconElement: (
                                                    <IconChevronUpTN />
                                                ),
                                            },
                                        },
                                    }),
                                ],
                            },
                        }),

                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                flex: {
                                    default: {
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'thor',
                                        props: {
                                            hasBackgroundFill: false,
                                            containerWidth:
                                                metrics.thenightly.siteMetrics
                                                    .mainContentWidth,
                                            horizontalGutters: false,
                                            verticalGutters: false,
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'taboola',
                                                    props: {
                                                        taboolaContainerId:
                                                            'taboola-below-article-thumbnails',
                                                        locationType: 'article',
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}

const getArticleFollowers = (inApp: boolean): CheckedComponentInformation[] => [
    layout.component({
        type: 'ad-unit',
        props: {
            noticePosition: 'below-center',
            hiddenUntilLoaded: false,
            padding: [0, 0, metrics.thenightly.margins.xxl, 0],
            slot: {
                id: 'leaderboard-two',
                size: 'leaderboard768Above',
            },
            adType: 'inline',
            lazyLoadingDistance: 0,
        },
    }),
    ...(inApp ? [] : [getLatestEditionSegment('article')]),
]

const relatedContent = (id: string) =>
    layout.nestedComposition({
        type: 'box',
        props: {
            flex: {
                default: {
                    flexDirection: 'column',
                    flexGrow: 0.5,
                },
            },
        },
        contentAreas: {
            main: [
                layout.nestedComposition({
                    type: 'component-overrider',
                    props: {
                        override: () => theNightlySmallSectionHeaderOverride,
                    },
                    contentAreas: {
                        children: [
                            layout.component({
                                type: 'publication-related-content',
                                props: {
                                    title: 'More like this',
                                    displayMode: 'juliet',
                                    numberOfItems: 5,
                                    sectionHeaderAs: 'h4',
                                    itemHeaderAs: 'h5',
                                    dataDefinitionArgs: {
                                        publicationId: id,
                                    },
                                },
                            }),
                            stickyAdUnit(),
                        ],
                    },
                }),
            ],
        },
    })

export const newsletterBanner = () =>
    layout.nestedComposition({
        type: 'box',
        props: {
            verticalSpacing: 'xxl',
            flex: {
                default: {
                    flexDirection: 'column',
                    flexGrow: 0.5,
                },
            },
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'tn-newsletter-signup',
                    props: {
                        text: `Sign up to The Nightly's newsletters.`,
                        subText:
                            'Get the first look at the digital newspaper, curated daily stories and breaking headlines delivered to your inbox.',
                        buttonLabel: 'Get the Newsletter',
                        mode: 'sidebar-article',
                    },
                }),
            ],
        },
    })

const stickyAdUnit = () =>
    layout.nestedComposition({
        type: 'box',
        props: {
            stickyOffset: 'calc(var(--stickyHeaderHeight) + 32px)',
            hasBackgroundFill: true,
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'ad-unit',
                    props: {
                        noticePosition: 'below-center',
                        slot: {
                            id: 'sidebar-one',
                            size: 'above1280MrecHalfPage',
                        },
                        adType: 'inline',
                        padding: [0, 0, 32, 0],
                    },
                }),
            ],
        },
    })

const getElectionHeader = (
    article: ArticleLikeV4DTO,
    enableElectionNavigation: string | boolean | undefined,
    enableElectionSectionHeadToHead: string | boolean | undefined,
    enableElectionSectionHeadToHeadCountdown: string | boolean | undefined,
    electionDefinition: ElectionDefinition,
) => {
    const { electionId } = electionDefinition
    const electionTopic = `politics/${electionId}`

    if (
        !enableElectionNavigation ||
        (article.topics.primary.id !== electionTopic &&
            !article.topics.secondary.some(
                (topic) => topic.id === electionTopic,
            ))
    ) {
        return []
    }

    return [
        layout.nestedComposition({
            type: 'box',
            props: {
                containerWidth: metrics.thenightly.siteMetrics.mainContentWidth,
                fullWidth: true,
                verticalSpacing: 'outerMargin',
                flex: {
                    //adjust top margin to offset page container padding
                    default: {
                        flexDirection: 'column',
                        marginTop: calcRem(-40),
                    },
                    md: {
                        marginTop: calcRem(-48),
                    },
                    lg: {
                        marginTop: calcRem(-80),
                    },
                },
                breakpoints: {},
            },
            contentAreas: {
                main: [
                    ...(enableElectionSectionHeadToHead
                        ? [
                              layout.component({
                                  type: 'election-head-to-head-seat-count-widget',
                                  props: {
                                      electionDefinition,
                                      countdownEnabled:
                                          enableElectionSectionHeadToHeadCountdown as boolean,
                                  },
                              }),
                          ]
                        : []),
                    layout.component({
                        type: 'election-navigation',
                        props: {
                            electionDefinition,
                        },
                    }),
                ],
            },
        }),
    ]
}
